import { decodeBytes } from "open-signer-shared-cryptography";
import { z } from "zod/v4";
import type { AuthData, DeviceData } from "../../types";
import type { CrossmintApiService } from "../api";
import { secureClearObject } from "../common/secure-memory";
import type { EncryptionService } from "../encryption";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { DeviceCache } from "../storage";

const DecryptedMasterSecretSchema = z.object({
	bytes: z.string(),
	encoding: z.literal("base64"),
});

export class SecretService extends CrossmintFrameService {
	name = "Secret Service";
	log_prefix = "[SecretService]";
	/** Recovery method whose OTP is pending per device, from start-onboarding until it completes. */
	private readonly pendingOnboarding = new Map<string, string>();

	constructor(
		private readonly cache: DeviceCache,
		private readonly encryptionService: EncryptionService,
		private readonly api: CrossmintApiService,
		protected readonly logger: FrameLogger,
	) {
		super(logger);
	}

	/**
	 * Retrieves and decrypts the master secret for a given device.
	 *
	 * The master secret is derived per recovery method, and the relay keeps one record per
	 * (device, recovery method). When `authId` is given the relay is asked for that method's
	 * record only; a record labelled with another method is never used to sign for `authId`.
	 *
	 * @param deviceId - The device ID to fetch the master secret for
	 * @param authData - Authentication data containing JWT and API key
	 * @param authId - Recovery method the secret is needed for, when known
	 * @returns The decrypted master secret as Uint8Array, or null if no usable secret is stored
	 */
	public async getMasterSecret(
		deviceId: string,
		authData: AuthData,
		authId?: string,
	): Promise<Uint8Array | null> {
		const deviceInfo = await this.cache.get(deviceId, authData, authId);
		if (deviceInfo == null) {
			this.logger.debug(this.log_prefix, `No data in cache`);
			return null;
		}

		this.logger.info(
			this.log_prefix,
			`Retrieved device info for device ${deviceId}`,
		);
		return this.format(deviceInfo);
	}

	/**
	 * Retrieves and decrypts the master secret for an export.
	 *
	 * Unlike {@link getMasterSecret}, this bypasses the {@link DeviceCache} and
	 * hits the dedicated, never-cached `/devices/:id/export` endpoint, which also
	 * records the export server-side (audit log + `signer.exported` webhook). The
	 * wallet address and signer locator are forwarded so the relay can attribute
	 * the export.
	 *
	 * @param deviceId - The device ID to fetch the master secret for
	 * @param exportInfo - Wallet address + signer locator forwarded to the relay
	 * @param authData - Authentication data containing JWT and API key
	 * @returns The decrypted master secret as Uint8Array
	 */
	public async getMasterSecretForExport(
		deviceId: string,
		exportInfo: { walletAddress?: string; authId?: string },
		authData: AuthData,
	): Promise<Uint8Array> {
		const deviceInfo = await this.api.exportDevice(
			deviceId,
			exportInfo,
			authData,
		);
		this.logger.info(
			this.log_prefix,
			`Retrieved device info for export of device ${deviceId}`,
		);
		return this.format(deviceInfo);
	}

	/**
	 * Caches a freshly onboarded record under `authId` and returns its decrypted master secret.
	 */
	public setMasterSecret(
		deviceId: string,
		data: DeviceData,
		authData: AuthData,
		authId?: string,
	): Promise<Uint8Array> {
		this.cache.set(deviceId, data, authData, authId);
		return this.format(data);
	}

	/**
	 * Remembers which recovery method an OTP was requested for, so complete-onboarding can
	 * tell the relay which method the resulting record belongs to.
	 */
	public markOnboardingStarted(deviceId: string, authId: string): void {
		this.pendingOnboarding.set(deviceId, authId);
	}

	/** Recovery method of the pending onboarding, if one was started in this frame. */
	public pendingOnboardingAuthId(deviceId: string): string | undefined {
		return this.pendingOnboarding.get(deviceId);
	}

	public markOnboardingCompleted(deviceId: string): void {
		this.pendingOnboarding.delete(deviceId);
	}

	private async format(deviceInfo: DeviceData) {
		const decryptedData = await this.encryptionService.decrypt<
			Record<string, unknown>,
			string
		>(
			deviceInfo.encryptedMasterSecret.ciphertext,
			deviceInfo.encryptedMasterSecret.encapsulatedKey,
		);

		try {
			const { bytes, encoding } =
				DecryptedMasterSecretSchema.parse(decryptedData);

			return decodeBytes(bytes, encoding);
		} finally {
			secureClearObject(decryptedData);
		}
	}
}
